import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Layers,
  Boxes,
  Scissors,
  Tag,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Facilitez vos préparatifs et évitez les déplacements inutiles
            dans le centre-ville ou les quais de Seine. Nous livrons
            l&apos;ensemble de vos cartons et fournitures directement à
            votre adresse à {city.name} et partout en Île-de-France. Grâce
            à notre propre flotte de transport, nous vous assurons un
            service de proximité fiable, rapide et adapté aux accès de
            votre quartier. Recevez votre matériel directement chez vous et
            commencez à emballer vos affaires sans stress.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Propre flotte locale",
              body: "Nos chauffeurs francilliens connaissent les accès, voies réservées et zones piétonnes de votre quartier.",
              Icon: Truck,
            },
            {
              title: "Cartons sains et secs",
              body: "Transport en véhicules fermés, à l'abri de la pluie et de la poussière des chantiers urbains.",
              Icon: ShieldCheck,
            },
            {
              title: "Créneaux respectés",
              body: "Vous fixez une plage horaire, nous la respectons à la minute pour ne pas bloquer votre planning.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Matériel de protection ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection professionnelle
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Matériel de protection pour vos déménagements à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un déménagement réussi dans les rues d&apos;{city.name} ne se
            limite pas aux cartons. Pour sécuriser vos meubles et objets
            précieux lors du transport, notre boutique vous propose une
            gamme complète d&apos;accessoires de protection professionnels.
            Rouleaux de film bulle haute densité, adhésifs ultra-résistants,
            housses de matelas et couvertures de protection sont à votre
            disposition. Protégez efficacement chaque volume contre les
            chocs et la poussière, pour un transfert en toute sérénité
            depuis ou vers {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              title: "Film bulle haute densité",
              body: "Rouleaux de 10 m à 100 m pour envelopper la vaisselle, l'électronique et les bibelots.",
              Icon: Layers,
              to: "/emballage-protection",
            },
            {
              title: "Adhésifs et pistolets",
              body: "Rubans techniques + dévidoirs ergonomiques pour sceller vos cartons en une main.",
              Icon: Scissors,
              to: "/fermeture-accessoires",
            },
            {
              title: "Housses matelas",
              body: "1 place, 2 places et king size pour protéger la literie contre la poussière et l'humidité.",
              Icon: ShieldCheck,
              to: "/emballage-protection",
            },
            {
              title: "Couvertures de déménagement",
              body: "Pour caler vos meubles dans le camion et éviter les frottements en virage.",
              Icon: Boxes,
              to: "/emballage-protection",
            },
          ].map(({ title, body, Icon, to }) => (
            <Link
              key={title}
              to={to}
              className="block bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[14px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </Link>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-asnieres-cta-protection"
          >
            Voir tout l&apos;emballage et la protection
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Conseils d'experts (3 astuces numérotées) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Tag className="h-3.5 w-3.5" strokeWidth={2.6} />
            Astuces de pro
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Conseils d&apos;experts pour bien préparer vos cartons de
            déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Réussir l&apos;emballage de ses biens demande de l&apos;organisation,
            surtout pour un déménagement en milieu urbain. Voici nos astuces
            de professionnels pour optimiser vos préparatifs.
          </p>
        </div>

        <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "01",
              title: "Répartissez le poids intelligemment",
              body: `Placez les objets lourds (comme les livres) au fond de petits cartons, et gardez les grands volumes pour les affaires légères (vêtements, couettes). Cela évite les déchirures lors du transport dans les escaliers ou les ascenseurs d'${city.name}.`,
            },
            {
              n: "02",
              title: "Sécurisez le fragile en priorité",
              body: "Enveloppez chaque assiette et verre individuellement dans notre film bulle avant de les caler solidement. Ne laissez aucun espace vide dans vos cartons pour empêcher les objets de bouger pendant le trajet.",
            },
            {
              n: "03",
              title: "Identifiez clairement vos cartons",
              body: "Notez la pièce de destination et le contenu sur le dessus et les côtés de chaque boîte. Cette méthode facilite le travail des déménageurs et vous fait gagner un temps précieux à l'arrivée dans votre nouveau logement.",
            },
          ].map(({ n, title, body }) => (
            <li
              key={n}
              className="relative bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="absolute -top-4 left-5 inline-flex items-center justify-center h-9 px-3 rounded-full bg-[#FF7A00] text-white font-display font-extrabold text-[13px] tracking-[0.1em]">
                ASTUCE {n}
              </div>
              <h3 className="mt-3 font-display font-extrabold text-[16px] text-[#0f1d2b]">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-asnieres-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-asnieres-cta-kits"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Choisir un kit complet
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-asnieres-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
